/**
 * Utilidades para el módulo Préstamos Parametrizados (pre-aprobados de la app).
 */
import Swal from "sweetalert2";

export const TITULO_MAX = 100;
export const DESCRIPCION_MAX = 1000;

export const MODOS = {
  AMBOS: "AMBOS",
  SOLO_PARAMETRIZADO: "SOLO_PARAMETRIZADO",
};

export const MODO_LABEL = {
  AMBOS: "Este + disponible",
  SOLO_PARAMETRIZADO: "Solo este préstamo",
};

export const ALCANCES = {
  GRUPO: "GRUPO",
  CLIENTES: "CLIENTES",
};

export const REQUIERE_DISPONIBLE_LABEL = {
  S: "Requiere disponible",
  N: "Sin disponible",
};

/**
 * Extrae el array de la respuesta (axios). Acepta los dos formatos de la API:
 *  - responseHandler: { status, success, message, data: [...] }
 *  - legacy:          { code, resp, result: [...] }
 *    (por compatibilidad con endpoints que todavía responden en formato legacy)
 */
export function extraerLista(res) {
  const body = res?.data ?? res;
  const candidatos = [body?.data, body?.result, body];
  return candidatos.find(Array.isArray) ?? [];
}

export const formatPesos = (valor) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(Number(valor) || 0);

export const textoRangoScore = (min, max) =>
  min === null || min === undefined ? "Todos" : `${min} – ${max}`;

/** Qué ve el socio en la app según la configuración. */
export function textoVisibilidad(modo, requiereDisponible, montoVariable = false) {
  const conDisp =
    modo === MODOS.AMBOS
      ? "ve este préstamo y también el préstamo por disponible"
      : "ve solo este préstamo (el préstamo por disponible se oculta)";
  const sinDisp =
    requiereDisponible === "N" && !montoVariable
      ? "ve este préstamo igual"
      : "no ve este préstamo";
  return { conDisp, sinDisp };
}

/** Texto de "a quién se le ofrece" para la tabla. */
export function textoDestinatarios(p) {
  if (p.alcance === ALCANCES.CLIENTES) {
    const n = Number(p.cantidadClientes ?? 0);
    return `${n} cliente${n !== 1 ? "s" : ""}`;
  }
  return p.nombreGrupo ?? `Grupo ${p.idGrupoAfinidad}`;
}

/**
 * Normaliza el error de la API (vía proxy IntraNode).
 * IntraNode devuelve: { data: { error: 1, error_detalle: <body API> }, statusCode }
 * y la API: { status, success, message, errorCode?, data? }.
 */
export function getApiError(err, fallback = "Ocurrió un error.") {
  const body = err?.response?.data;
  const det = body?.data?.error_detalle ?? body?.error_detalle ?? body;
  const detalle = det && typeof det === "object" ? det : null;

  let message =
    detalle?.message ??
    body?.resp ??
    (typeof det === "string" ? det : null) ??
    fallback;
  // La API antepone el código ("CONFLICTO_CONFIGURACION: ...")
  message = String(message).replace(/^[A-Z_]+:\s*/, "");

  return {
    status: err?.response?.status ?? null,
    message,
    conflictos: Array.isArray(detalle?.data?.conflictos)
      ? detalle.data.conflictos
      : [],
  };
}

const escapeHtml = (s) =>
  String(s ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c],
  );

/**
 * Muestra el error. Si es un conflicto de configuración (409) lista los
 * préstamos que chocan y explica por qué no se puede guardar.
 */
export function mostrarErrorApi(err, fallback) {
  const { message, conflictos } = getApiError(err, fallback);

  if (conflictos.length === 0) {
    return Swal.fire("Error", message, "error");
  }

  const porClientes = conflictos.some((c) => c.alcance === ALCANCES.CLIENTES);
  const filas = conflictos
    .map(
      (c) => `
        <tr>
          <td style="padding:4px 8px;text-align:left">#${escapeHtml(c.id)} ${escapeHtml(c.titulo)}</td>
          <td style="padding:4px 8px">${escapeHtml(
            c.alcance === ALCANCES.CLIENTES
              ? `${c.clientesEnComun ?? 0} en común`
              : textoRangoScore(c.scoreMin, c.scoreMax),
          )}</td>
          <td style="padding:4px 8px">${escapeHtml(MODO_LABEL[c.modo] ?? c.modo)}</td>
          <td style="padding:4px 8px">${escapeHtml(REQUIERE_DISPONIBLE_LABEL[c.requiereDisponible] ?? c.requiereDisponible)}</td>
        </tr>`,
    )
    .join("");

  return Swal.fire({
    icon: "warning",
    title: "No se puede guardar",
    width: 720,
    html: `
      <p style="text-align:left;font-size:14px;margin-bottom:12px">${escapeHtml(message)}</p>
      <table style="width:100%;font-size:13px;border-collapse:collapse">
        <thead>
          <tr style="background:#f3f4f6">
            <th style="padding:4px 8px;text-align:left">Préstamo en conflicto</th>
            <th style="padding:4px 8px">${porClientes ? "Clientes" : "Score"}</th>
            <th style="padding:4px 8px">Muestra</th>
            <th style="padding:4px 8px">Disponible</th>
          </tr>
        </thead>
        <tbody>${filas}</tbody>
      </table>`,
    confirmButtonText: "Entendido",
    confirmButtonColor: "#56C401",
  });
}

/** Ordena por grupo y después por score (sin rango al final), como adelantos. */
export function ordenarPrestamos(lista) {
  return [...lista].sort((a, b) => {
    if (a.alcance !== b.alcance) return a.alcance === ALCANCES.CLIENTES ? 1 : -1;
    const g = String(a.nombreGrupo ?? "").localeCompare(String(b.nombreGrupo ?? ""));
    if (g !== 0) return g;
    if (a.scoreMin === null && b.scoreMin === null) return 0;
    if (a.scoreMin === null) return 1;
    if (b.scoreMin === null) return -1;
    return a.scoreMin - b.scoreMin;
  });
}
